import type { TopOfMindResponse, TopOfMindStatus } from '@wundo/shared/topOfMind.ts';
import { Badge, type BadgeTone } from '../badge/badge.tsx';
import { Button } from '../button/button.tsx';
import { Card, CardTitle } from '../card/card.tsx';
import { ChatMarkdown } from '../chat/chat-markdown.tsx';
import { Spinner } from '../spinner/spinner.tsx';
import { InfoTip } from '../tooltip/tooltip.tsx';
import {
  DEFAULT_TOP_OF_MIND_PANEL_LABELS,
  type TopOfMindHolderKind,
  type TopOfMindPanelLabels,
} from './top-of-mind-panel-labels.ts';

export interface TopOfMindPanelProps {
  state: TopOfMindResponse;
  holder: TopOfMindHolderKind;
  labels?: Partial<TopOfMindPanelLabels>;
  /** How a timestamp reads; the host knows the reader's locale. */
  formatDate?: (iso: string) => string;
  /** Offers the rebuild button; it shows only while documents are linked. */
  onRebuild?: () => void;
  /** A rebuild was asked for and has not settled into ready or failed yet. */
  rebuilding?: boolean;
}

const STATUS_TONE: Record<Exclude<TopOfMindStatus, 'none'>, BadgeTone> = {
  scheduled: 'neutral',
  generating: 'info',
  ready: 'success',
  failed: 'danger',
};

function defaultFormatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

function statusNote(state: TopOfMindResponse, text: TopOfMindPanelLabels): string | null {
  switch (state.status) {
    case 'none':
      return text.noneNote;
    case 'scheduled':
      return text.scheduledNote;
    case 'generating':
      return text.generatingNote;
    case 'failed':
      return text.failedNote;
    case 'ready':
      return null;
  }
}

/**
 * The short summary of a track's or cohort's documents that the assistant
 * reads. It is rewritten on its own whenever the documents change; the
 * rebuild only skips the wait.
 */
export function TopOfMindPanel({
  state,
  holder,
  labels,
  formatDate = defaultFormatDate,
  onRebuild,
  rebuilding = false,
}: TopOfMindPanelProps) {
  const text = { ...DEFAULT_TOP_OF_MIND_PANEL_LABELS, ...labels };
  const note = statusNote(state, text);
  const waiting = state.documents_processing > 0;
  const canRebuild = onRebuild !== undefined && state.documents_linked > 0;

  return (
    <Card as='section' aria-label={text.title} className='flex flex-col gap-3'>
      <div className='flex items-center justify-between gap-3'>
        <div className='flex items-center gap-1'>
          <CardTitle>{text.title}</CardTitle>
          <InfoTip content={text.info({ holder })} />
        </div>
        <div className='flex items-center gap-2'>
          {state.status !== 'none' && (
            <Badge tone={STATUS_TONE[state.status]} className='gap-1'>
              {state.status === 'generating' && <Spinner className='size-2.5' />}
              {text.status[state.status]}
            </Badge>
          )}
          {canRebuild && (
            <Button
              variant='quiet'
              size='sm'
              loading={rebuilding}
              disabled={state.status === 'generating' || waiting}
              onClick={onRebuild}
            >
              {text.rebuild}
            </Button>
          )}
        </div>
      </div>

      {note !== null && <p className='text-sm text-ink-muted'>{note}</p>}
      {waiting && (
        <p className='text-sm text-ink-muted'>
          {text.waitingForDocuments({ count: state.documents_processing })}
        </p>
      )}

      {state.content !== null && (
        <ChatMarkdown className='text-sm text-ink-body'>{state.content}</ChatMarkdown>
      )}

      {state.generated_at !== null && (
        <p className='text-xs text-ink-muted'>
          {text.generatedAt({ date: formatDate(state.generated_at) })}
        </p>
      )}
    </Card>
  );
}
